import React, { useEffect, useState } from 'react';
import API from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import {
    ArrowRight,
    BadgeCheck,
    HeartHandshake,
    LockKeyhole,
    ShieldCheck,
    UsersRound,
} from 'lucide-react';
import Navbar from '../../components/Navbar/Navbar';
import Hero from '../../components/Hero/Hero';
import ProfilesSection from '../../components/Profiles/ProfilesSection';
import GuestFeatured from '../../components/Profiles/GuestFeatured';
import ServicesSection from '../../components/Services/ServicesSection';
import SuccessStoriesSection from '../../components/SuccessStories/SuccessStoriesSection';
import StepsRow from '../../components/Showcase/StepsRow';
import FaqSection from '../../components/Showcase/FaqSection';
import BlogSection from '../../components/Showcase/BlogSection';
import CommunityLinks from '../../components/Showcase/CommunityLinks';
import Footer from '../../components/Footer/Footer';
import LoginModal from '../../components/Modals/LoginModal';
import RegisterModal from '../../components/Modals/RegisterModal';
import BannerSlider from '../../components/BannerSlider/BannerSlider';
import { useAuth } from '../../context/AuthContext';
import './Home.css';
import './WeddingTheme.css';
import './HomeBanners.css';
import './HomeLayout.css';

// The home page keeps these sections, in this order:
//   hero · stats · featured matches · wedding services · why us · love stories · steps · FAQ · call to action
// (the extra sections built earlier, such as plans, FAQ, privacy and support, still exist as components
// under components/Showcase and can be added back with one line each).
const WHY_ITEMS = [
    { icon: BadgeCheck, title: 'Verified Profiles', text: 'Genuine and trusted members' },
    { icon: HeartHandshake, title: 'Smart Matching', text: 'Find compatible matches easily' },
    { icon: UsersRound, title: 'Complete Wedding Support', text: 'Connect with trusted service providers' },
    { icon: LockKeyhole, title: 'Privacy & Security', text: 'Your data is safe with us' },
];

// The three numbers come from the server (typed by the admin, or counted live); these are the fallback while loading.
const STAT_DEFAULTS = { activeProfiles: '50K+', verifiedMembers: '100%', happyMarriages: '5K+' };
const buildStats = (v) => [
    { icon: UsersRound, value: v.activeProfiles, label: 'Active profiles' },
    { icon: BadgeCheck, value: v.verifiedMembers, label: 'Verified members' },
    { icon: HeartHandshake, value: v.happyMarriages, label: 'Happy marriages' },
    { icon: ShieldCheck, value: 'Private', label: 'Secure by design' },
];

const Home = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [showLogin, setShowLogin] = useState(false);
    const [showRegister, setShowRegister] = useState(false);
    const [statValues, setStatValues] = useState(STAT_DEFAULTS);
    useEffect(() => {
        API.get('/site-stats').then(res => setStatValues(v => ({ ...v, ...res.data }))).catch(() => { });
    }, []);
    const STATS = buildStats(statValues);

    const openRegister = () => setShowRegister(true);
    const openLogin = () => setShowLogin(true);

    return (
        <div className={`home-page ${user ? 'home-page--member' : 'home-page--guest'}`}>
            <Navbar onLoginClick={openLogin} onRegisterClick={openRegister} />

            <main style={{ display: 'contents' }}>
            {/* 1. Hero with the match search */}
            <Hero onRegisterClick={openRegister} onLoginClick={openLogin} user={user} />

            {/* 2. Stats strip */}
            <section className="home-proof" aria-label="Gettimelam at a glance">
                <div className="home-proof__inner">
                    {STATS.map(item => (
                        <div className="home-proof__item" key={item.label}>
                            <item.icon size={20} />
                            <span><strong>{item.value}</strong><small>{item.label}</small></span>
                        </div>
                    ))}
                </div>
            </section>

            {/* 3. Featured matches: real suggestions for members, locked previews for visitors */}
            {user ? <ProfilesSection onLoginClick={openLogin} /> : <GuestFeatured onRegisterClick={openRegister} />}

            {/* 4. Complete wedding services (photo tiles from the admin Service cards) */}
            <ServicesSection onLoginClick={openLogin} />

            {/* Middle banners from the admin panel (shown to visitors and members) */}
            <section className="home-banner-stage" aria-labelledby="featured-promotions-title">
                <header className="home-banner-stage__heading">
                    <span>Handpicked for You</span>
                    <h2 id="featured-promotions-title">Discover More for Your <em>Wedding Journey</em></h2>
                    <p>Explore helpful services, special offers and trusted experiences created for every step of your celebration.</p>
                </header>
                <BannerSlider position="middle" />
            </section>

            {/* 5. Why Gettimelam */}
            <section className="home-why" aria-labelledby="why-title">
                <div className="home-why__inner">
                    <p className="home-why__eyebrow">Why Gettimelam</p>
                    <h2 id="why-title">More Than a Matrimony Site</h2>
                    <p className="home-why__lead">We bring together people, families and trusted wedding services in one place.</p>
                    <div className="home-why__grid">
                        {WHY_ITEMS.map(item => (
                            <article className="home-why__card" key={item.title}>
                                <span className="home-why__icon"><item.icon size={26} /></span>
                                <h3>{item.title}</h3>
                                <p>{item.text}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* 6. Love stories */}
            <SuccessStoriesSection />

            {/* 7. Simple steps */}
            <StepsRow />

            {/* 8. FAQ: the first 10 questions from the admin panel (hidden until some exist) */}
            <FaqSection />

            {/* 9. Latest blog posts from the admin panel (hidden until some are published) */}
            <BlogSection />

            {/* Links to the search pages (community + city, wedding services by city, Tamil) */}
            <CommunityLinks />

            {/* Bottom banners from the admin panel (shown to visitors and members) */}
            <BannerSlider position="bottom" />

            {/* 10. Call to action */}
            <section className="home-final-cta">
                <div>
                    <span className="home-kicker">{user ? 'Your next match is waiting' : 'Your story can begin here'}</span>
                    <h2>Ready to meet someone meaningful?</h2>
                    <p>{user ? 'Browse verified profiles chosen around what matters to you.' : 'Create your profile in a few minutes. Registration is free.'}</p>
                </div>
                <button type="button" onClick={user ? () => navigate('/browse') : openRegister}>
                    {user ? 'Browse your matches' : 'Create free profile'} <ArrowRight size={18} />
                </button>
            </section>

            </main>

            <Footer />

            {showLogin && (
                <LoginModal
                    onClose={() => setShowLogin(false)}
                    onSwitchToRegister={() => { setShowLogin(false); setShowRegister(true); }}
                />
            )}
            {showRegister && (
                <RegisterModal
                    onClose={() => setShowRegister(false)}
                    onSwitchToLogin={() => { setShowRegister(false); setShowLogin(true); }}
                />
            )}
        </div>
    );
};

export default Home;
